import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  BulkUpdateVariantPricesDTO,
  CreateCategoryDTO,
  CreateVariantDTO,
  DuplicateConvertBatchDTO,
  DuplicateConvertDTO,
  DuplicateConvertPreviewDTO,
  SyncProductVariantsDTO,
  UpdateCategoryDTO,
  UpdateVariantDTO,
} from "@/modules/products/domain/contracts";
import {
  type ProductFormPayload,
  toProductApiPayload,
} from "../lib/split-product-form";
import { type ProductListQuery, type VariantPriceEditListQuery, productsApi } from "./client";

const keys = {
  all: ["products"] as const,
  list: (query?: ProductListQuery) => [...keys.all, "list", query] as const,
  variantPrices: (query?: VariantPriceEditListQuery) =>
    [...keys.all, "variant-prices", query] as const,
  categories: () => [...keys.all, "categories"] as const,
  detail: (id: string) => [...keys.all, "detail", id] as const,
  picker: (query?: { categoryId?: string; search?: string }) =>
    [...keys.all, "picker", query] as const,
  variantDeletePreview: (variantId: string) =>
    [...keys.all, "variant-delete-preview", variantId] as const,
  duplicateConvertPreview: (input: DuplicateConvertPreviewDTO | null) =>
    [...keys.all, "duplicate-convert-preview", input] as const,
};

export function useProductCategoriesQuery() {
  return useQuery({
    queryKey: keys.categories(),
    queryFn: () => productsApi.listCategories(),
  });
}

export function useProductsQuery(query?: ProductListQuery) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => productsApi.list(query),
  });
}

export function useVariantPriceEditList(query?: VariantPriceEditListQuery) {
  return useQuery({
    queryKey: keys.variantPrices(query),
    queryFn: () => productsApi.listVariantsForPriceEdit(query),
  });
}

export function useBulkUpdateVariantPrices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BulkUpdateVariantPricesDTO) =>
      productsApi.bulkUpdateVariantPrices(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

export function useProductQuery(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => productsApi.get(id),
    enabled: Boolean(id),
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCategoryDTO) => productsApi.createCategory(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.categories() }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } & UpdateCategoryDTO) =>
      productsApi.updateCategory(input.id, {
        name: input.name,
        description: input.description,
        iconKey: input.iconKey,
        sortOrder: input.sortOrder,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.categories() });
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

/** สร้างสินค้า (เฉพาะฟิลด์สินค้า — ไม่ sync variants) */
export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProductFormPayload) => {
      return productsApi.create(toProductApiPayload(input));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
    },
  });
}

/** อัปเดตสินค้า (เฉพาะฟิลด์สินค้า — ไม่ sync variants) */
export function useUpdateProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ProductFormPayload) => {
      return productsApi.update(id, toProductApiPayload(input));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useSyncProductVariants(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SyncProductVariantsDTO) =>
      productsApi.syncVariants(productId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(productId) });
    },
  });
}

export function useCreateVariant(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVariantDTO) =>
      productsApi.createVariant(productId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(productId) });
    },
  });
}

export function useUpdateVariant(productId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { variantId: string } & UpdateVariantDTO) => {
      const { variantId, ...body } = input;
      return productsApi.updateVariant(variantId, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      if (productId) {
        qc.invalidateQueries({ queryKey: keys.detail(productId) });
      }
    },
  });
}

export function useDeleteVariant(productId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) => productsApi.deleteVariant(variantId),
    onSuccess: (_data, variantId) => {
      // ลบ cache preview ทันที — กัน refetch แล้วได้ 404 toast
      qc.removeQueries({
        queryKey: keys.variantDeletePreview(variantId),
      });
      if (productId) {
        qc.invalidateQueries({ queryKey: keys.detail(productId) });
      }
      qc.invalidateQueries({
        queryKey: keys.all,
        predicate: (query) => query.queryKey[1] !== "variant-delete-preview",
      });
    },
  });
}

export function useVariantDeletePreview(variantId: string | null) {
  return useQuery({
    queryKey: keys.variantDeletePreview(variantId ?? ""),
    queryFn: () => {
      if (!variantId) throw new Error("Missing variantId");
      return productsApi.getVariantDeletePreview(variantId);
    },
    enabled: Boolean(variantId),
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useAdjustStock() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      variantId: string;
      quantityDelta: number;
      note?: string;
      lotPicks?: { stockLotId: string; quantity: number }[];
    }) =>
      productsApi.adjustStock(input.variantId, {
        quantityDelta: input.quantityDelta,
        note: input.note,
        lotPicks: input.lotPicks,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}

export function useDuplicateConvertPreview(
  input: DuplicateConvertPreviewDTO | null,
) {
  return useQuery({
    queryKey: keys.duplicateConvertPreview(input),
    queryFn: () => {
      if (!input) throw new Error("Missing duplicate convert preview input");
      return productsApi.duplicateConvertPreview(input);
    },
    enabled: Boolean(input),
  });
}

export function useDuplicateConvertUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DuplicateConvertDTO) =>
      productsApi.duplicateConvert(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}

export function useDuplicateConvertBatchUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DuplicateConvertBatchDTO) =>
      productsApi.duplicateConvertBatch(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: ["inventory"] });
    },
  });
}
